import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSearchParams } from "react-router-dom";

import { SelectWebsiteScrapeProduct } from "@/components/SelectWebsiteScrapeProduct/SelectWebsiteScrapeProduct";

import { ScrapeLevel } from "@/enum/job.enum";

import {
	useGetProfilesQuery,
	useGetProfilesWithAdminPermissionsQuery,
	useGetProfilesWithLiSnQuery,
} from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { CustomEventType } from "@/enum/custom-event.enum";

import {
	useFinalizeCheckoutSessionForWebsiteScrapeMutation,
	useGetWebsiteScrapeCreditsQuery,
} from "@/pages/Private/redux/websiteScrape/websiteScrape.api";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import { createNewListSettings } from "./config/tileConfig";
import { CatalogTile, CatalogTileRequirement, getCatalogTiles } from "./config/tileCatalog";
import { getExampleTileIds } from "./config/catalogExamples";
import {
	ALL_CATEGORIES,
	CatalogCategorySelection,
	countCatalogTiles,
	FAVORITES_CATEGORY,
	filterCatalogTilesByCategory,
} from "./utils/catalogFilters";
import { isWebsiteScrapePurchaseRequired } from "./utils/catalogAvailability";
import { AiSearchBar } from "./components/AiSearchBar";
import { CatalogRail } from "./components/CatalogRail";
import { CatalogGrid } from "./components/CatalogGrid";
import { CatalogSections } from "./components/CatalogSections";
import { CatalogFavoritesEmpty } from "./components/CatalogFavoritesEmpty";
import { useCatalogFavorites } from "./hooks/useCatalogFavorites";
import { useCreateListFromTile } from "./hooks/useCreateListFromTile";
import { FeaturesCodes } from "../../../Users/enum/codes";

export type SubType = CsvEnrichmentType | CustomEventType | ScrapeLevel | undefined;

/** Height (px) of the sticky ListBuilderLayout header bar (`h-12`). */
const LIST_BUILDER_HEADER_HEIGHT_PX = 48;
/**
 * Overlap (px) the pinned rail tucks *under* the search block's bottom. Below `md` the rail pins as
 * a full-width bar directly beneath the search block while catalog tiles scroll under both; a
 * positive gap would leave an uncovered seam through which those tiles peek. A small overlap closes
 * it — the search block (`z-20`) paints over these px (invisible: it lands in the block's `pb-6`
 * white zone), above the rail (`z-10`).
 */
const RAIL_STICKY_OVERLAP_PX = 2;

export const ListCreate: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.${key}`);

	const [finalizeCheckoutSession] = useFinalizeCheckoutSessionForWebsiteScrapeMutation();
	const { data: websiteScrapeCredits } = useGetWebsiteScrapeCreditsQuery();
	const isTrackerAsListActive = useIsFeatureActive(FeaturesCodes.TRACKER_AS_LIST);
	const isSmartListActive = useIsFeatureActive(FeaturesCodes.SMART_LIST);
	const { createFromTile, isLoading: isCreatingList } = useCreateListFromTile();
	const { favorites, isFavorite, toggleFavorite } = useCatalogFavorites();

	const [searchParams, setSearchParams] = useSearchParams();
	const [query, setQuery] = useState("");
	const [activeCategory, setActiveCategory] = useState<CatalogCategorySelection>(ALL_CATEGORIES);
	// The example chip currently narrowing the catalog to a curated set of list types (null = none).
	const [activeExampleKey, setActiveExampleKey] = useState<string | null>(null);
	const [highlightedTileId, setHighlightedTileId] = useState<string | null>(null);
	const [showSelectWebsiteScrapeProduct, setShowSelectWebsiteScrapeProduct] = useState(false);

	// The page header (ListBuilderLayout) sticks at the top of the scroll viewport at h-12 (48px).
	// The AI search bar pins directly beneath it, and the catalog rail pins beneath the search bar —
	// so the rail's sticky offset must track the search bar's live height (it grows/shrinks with the
	// AI recommendations panel). Measure it and feed the offset to the rail.
	const searchBarRef = useRef<HTMLDivElement>(null);
	const [searchBarHeight, setSearchBarHeight] = useState(0);

	useEffect(() => {
		const element = searchBarRef.current;

		if (!element) {
			return;
		}

		// ResizeObserver fires once on observe() with the initial size, then on every change
		// (e.g. the AI recommendations panel expanding/collapsing) — so this is the sole source of
		// truth and avoids a direct set-state in the effect body.
		const observer = new ResizeObserver(() => setSearchBarHeight(element.offsetHeight));

		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	const railStickyTopPx = LIST_BUILDER_HEADER_HEIGHT_PX + searchBarHeight - RAIL_STICKY_OVERLAP_PX;

	useEffect(() => {
		const sessionId = searchParams.get("session_id");

		if (sessionId) {
			finalizeCheckoutSession({ sessionId })
				.unwrap()
				.then(() => {
					setSearchParams({}, { replace: true });
				});
			setSearchParams({}, { replace: true });
		}
	}, [finalizeCheckoutSession, searchParams, setSearchParams]);

	const catalogTiles = useMemo(() => {
		return getCatalogTiles(createNewListSettings).filter(
			(tile) =>
				(!tile.requirements.includes(CatalogTileRequirement.TRACKER_AS_LIST_FEATURE) ||
					isTrackerAsListActive) &&
				(!tile.requirements.includes(CatalogTileRequirement.SMART_LIST_FEATURE) ||
					isSmartListActive)
		);
	}, [isTrackerAsListActive, isSmartListActive]);

	const filteredTiles = useMemo(() => {
		// An active example chip pins the catalog to its curated tile set, in chip order (feature-gated
		// tiles absent from `catalogTiles` drop out). Otherwise fall back to category + search filtering.
		const exampleTileIds = getExampleTileIds(activeExampleKey);

		if (exampleTileIds) {
			return exampleTileIds
				.map((id) => catalogTiles.find((tile) => tile.id === id))
				.filter((tile): tile is CatalogTile => Boolean(tile));
		}

		return filterCatalogTilesByCategory(catalogTiles, query, activeCategory, favorites);
	}, [catalogTiles, query, activeCategory, favorites, activeExampleKey]);

	// Group the catalog into labelled per-category sections only on the unfiltered "All lists" view.
	// A specific category (the rail already names it), an active search, or an example chip renders a
	// flat grid of matches that cut across categories.
	const isGroupedView = !activeExampleKey && activeCategory === ALL_CATEGORIES && !query.trim();

	const categoryCounts = useMemo(
		() => countCatalogTiles(catalogTiles, favorites),
		[catalogTiles, favorites]
	);

	const { data: profiles } = useGetProfilesQuery({
		page: 1,
		limit: 200,
	});

	const { data: profilesWithLiSn } = useGetProfilesWithLiSnQuery({
		page: 1,
		limit: 200,
		hasLiSn: true,
	});

	const { data: profilesWithAdminPermissions } = useGetProfilesWithAdminPermissionsQuery({
		page: 1,
		limit: 200,
	});

	const selectTile = (tile: CatalogTile) => {
		setHighlightedTileId(tile.id);

		if (isWebsiteScrapePurchaseRequired(tile, websiteScrapeCredits?.credits)) {
			setShowSelectWebsiteScrapeProduct(true);

			return;
		}

		createFromTile(tile);
	};

	// Clicking an example chip pins the catalog to its curated set; clicking the active one again
	// clears it. Either way search/category reset so the chip's result isn't further narrowed.
	const selectExample = (key: string) => {
		setActiveExampleKey((current) => (current === key ? null : key));
		setQuery("");
		setActiveCategory(ALL_CATEGORIES);
		setHighlightedTileId(null);
	};

	const selectCategory = (category: CatalogCategorySelection) => {
		setActiveExampleKey(null);
		setActiveCategory(category);
	};

	return (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-8 overflow-x-clip" title={ts("create.title")}>
			<div className="w-full max-w-page">
				{/* Sticky top block: search bar + example chips, pinned flush beneath the h-12 header.
				    `-mt-4` cancels the body's `p-4` top padding and `pt-4` re-adds it inside the block, so
				    the block's resting position equals its pinned position — it sticks with no scroll jump.
				    The block's measured height drives the rail's sticky offset below. */}
				<div ref={searchBarRef} className="sticky top-12 z-20 -mt-4 bg-white pt-4 pb-6">
					<AiSearchBar
						activeExampleKey={activeExampleKey}
						query={query}
						onQueryChange={(value) => {
							setQuery(value);
							setActiveExampleKey(null);
							setHighlightedTileId(null);
						}}
						onSelectExample={selectExample}
					/>
				</div>
				<div className="flex flex-col items-start gap-7 md:flex-row">
					<CatalogRail
						activeCategory={activeCategory}
						counts={categoryCounts}
						stickyTopPx={railStickyTopPx}
						onSelectCategory={selectCategory}
					/>
					{/* `isolate` traps the catalog tiles' `z-100` (from their Tooltip trigger wrapper) inside
					    this content column, so they can't paint over the sticky search bar/header (any screen)
					    or the sticky category filter when it pins above them below `md`. The rail's own `z-10`
					    then sits above this isolated column. */}
					<div className="min-w-0 flex-1 isolate">
						{activeCategory === FAVORITES_CATEGORY && favorites.size === 0 ? (
							<CatalogFavoritesEmpty />
						) : isGroupedView ? (
							<CatalogSections
								isBusy={isCreatingList}
								availabilityContext={{
									profiles: profiles?.data,
									profilesWithAdminPermissions: profilesWithAdminPermissions?.data,
									profilesWithLiSn: profilesWithLiSn?.data,
									websiteScrapeCredits: websiteScrapeCredits?.credits,
								}}
								highlightedTileId={highlightedTileId}
								isFavorite={isFavorite}
								tiles={filteredTiles}
								onSelectTile={selectTile}
								onToggleFavorite={(tile) => toggleFavorite(tile.id)}
							/>
						) : (
							<CatalogGrid
								isBusy={isCreatingList}
								availabilityContext={{
									profiles: profiles?.data,
									profilesWithAdminPermissions: profilesWithAdminPermissions?.data,
									profilesWithLiSn: profilesWithLiSn?.data,
									websiteScrapeCredits: websiteScrapeCredits?.credits,
								}}
								highlightedTileId={highlightedTileId}
								isFavorite={isFavorite}
								tiles={filteredTiles}
								onSelectTile={selectTile}
								onToggleFavorite={(tile) => toggleFavorite(tile.id)}
							/>
						)}
					</div>
				</div>
			</div>
			{showSelectWebsiteScrapeProduct && (
				<SelectWebsiteScrapeProduct
					setShow={setShowSelectWebsiteScrapeProduct}
					show={showSelectWebsiteScrapeProduct}
				/>
			)}
		</ListBuilderLayout>
	);
};
